import { LogLevel } from '@cbsinteractive/avia-js';
import { shaka } from '@cbsinteractive/avia-js-shaka';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';

/**
 * @copyright © Paramount Streaming
 * @description Critical DASH video playback and DRM protection plugin for VTG Avia player
 *
 * **Critical Playback Infrastructure:**
 * ShakaPlugin provides DASH (Dynamic Adaptive Streaming over HTTP) video decode
 * capabilities and DRM content protection. This plugin is classified as CRITICAL -
 * video playback is impossible without it for DASH content.
 *
 * **Core Functionality:**
 * - **DASH Video Streaming**: Adaptive bitrate streaming for quality/bandwidth management
 * - **DRM Content Protection**: Widevine, PlayReady, FairPlay support for protected content
 * - **Adaptive Streaming**: Automatic quality switching based on network conditions
 * - **Protected Content**: Required for DRM-protected movie and TV content playback
 *
 * **Platform Configuration:**
 * - **Desktop/Mobile Web**: Full streaming configuration with retry logic and timeout handling
 * - **Smart TV/HTML5**: Reduced configuration for constrained device environments
 * - **HDR Support**: HDR video configuration for supported content and devices
 *
 * **VTG Integration:**
 * Uses VTG's avia-js-shaka plugin (v2.61.1) built on Shaka Player 4.16.24 for
 * video streaming with Smart Tag business logic integration.
 *
 * **Function:**
 * Required for DASH content playback, DRM-protected content access,
 * and content protection enforcement across streaming platforms.
 *
 * @see {@link https://github.com/shaka-project/shaka-player} Shaka Player Documentation
 * @see {@link https://paramount.atlassian.net/browse/PLPSCORE-224} HDR Implementation Details
 */
export class ShakaPlugin extends AviaPlugin implements IAviaPlugin {
	package = shaka;
	namespaceKey = 'plugin_shaka';

	/**
	 * @description Initializes critical DASH playback plugin for VTG Avia player
	 *
	 * **Critical Plugin Initialization:**
	 * Sets up Shaka Player integration as a critical component of the video playback pipeline.
	 * Failure to initialize this plugin will prevent DASH video content from playing.
	 *
	 * @param {ITagAbstractClassArgument} argument Tag instance for Smart Tag business logic integration
	 */
	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'ShakaPlugin'
			}
		);
	}

	/**
	 * @description Configures platform-optimized Shaka Player parameters for DASH video streaming
	 *
	 * **Platform-Specific Configuration Strategy:**
	 * This method implements Paramount's platform-optimized streaming strategy that adapts
	 * Shaka Player configuration based on deployment environment and device capabilities.
	 *
	 * **Configuration Elements:**
	 *
	 * **1. Dependency Management:**
	 * - **Dynamic Shaka Loading**: Loads Shaka Player from configured CDN with cache busting
	 * - **Version Control**: Ensures consistent Shaka Player version across deployments
	 * - **Development Support**: Cache busting for development and testing workflows
	 *
	 * **2. Platform-Specific Streaming Configuration:**
	 *
	 * **Desktop/Mobile Web (Full Configuration):**
	 * - **Streaming Resilience**: 1 retry attempt with 24-second timeout for network issues
	 * - **Error Recovery**: Balanced retry strategy preventing excessive attempts
	 * - **Premium Experience**: Full streaming feature set for primary platforms
	 *
	 * **Smart TV/HTML5 (Optimized Configuration):**
	 * - **Lightweight Setup**: Minimal configuration optimized for constrained environments
	 * - **Memory Efficiency**: Reduced feature set for Smart TV platform limitations
	 * - **Device Compatibility**: Configuration tested across TV manufacturer platforms
	 *
	 * **3. HDR Video Enhancement:**
	 * - **Premium Content Support**: HDR level configuration for supported content
	 * - **Device Capability Detection**: Adapts HDR settings based on device support
	 * - **Future Enhancement**: Prepared for advanced HDR content rollout
	 *
	 * **Business Benefits:**
	 * - **Streaming Reliability**: Platform-specific tuning reduces playback failures
	 * - **Premium Content Protection**: DRM integration enables subscription tier enforcement
	 * - **Cross-Platform Consistency**: Unified DASH streaming across all deployment targets
	 * - **Performance Optimization**: Configuration tuned for each platform's capabilities
	 *
	 * @returns {Record<string, any>} Platform-optimized Shaka Player configuration
	 */
	getFactoryParams() {
		// Shaka regular URL (minified)
		const shakaURL = this.utils.String.cleanUp(
			this.utils.URL.addCacheToken(
				this.tag?.model?.config?.tag?.Imports?.params?.low_level?.shaka?.url?.regular,
				this.runtimeVO?.debug?.cacheBust
			)
		);

		// Shaka debug URL (unminified) - constructed by replacing shaka-player.compiled.js with shaka-player.compiled.debug.js
		const shakaURLDebug = shakaURL?.replace( 'shaka-player.compiled.js', 'shaka-player.compiled.debug.js' );

		let shakaPluginConfig: Record<string, any> = {};

		const SHAKA_MANIFEST_MAX_ATTEMPTS = 2;
		const SHAKA_MANIFEST_BASE_DELAY_MS = 1000;
		const SHAKA_STREAMING_MAX_ATTEMPTS = 1;
		const SHAKA_STREAMING_TIMEOUT_MS = 24000;

		switch ( this?.params?.SYSTEM?.embedType ) {
			case EmbedType.DESKTOP:
			case EmbedType.MOBILE_WEB:
				shakaPluginConfig = {
					manifest: {
						retryParameters: {
							maxAttempts: SHAKA_MANIFEST_MAX_ATTEMPTS,
							baseDelay: SHAKA_MANIFEST_BASE_DELAY_MS
						}
					},
					streaming: {
						retryParameters: {
							maxAttempts: SHAKA_STREAMING_MAX_ATTEMPTS,
							timeout: SHAKA_STREAMING_TIMEOUT_MS
						}
					},
					preferredVideoHdrLevel: ''
				};
				break;
			case EmbedType.HTML5:
				shakaPluginConfig = {
					preferredVideoHdrLevel: ''
				};
				break;
			default:
				break;
		}

		return {
			...super.getFactoryParams(),

			dependencies: {
				// set shaka url based on debug flag
				shaka: this?.runtimeVO?.debug?.lowLevel ? shakaURLDebug : shakaURL
			},
			// debug field expects boolean; use logLevel to properly suppress console output in production
			logLevel: this?.runtimeVO?.debug?.lowLevel ? LogLevel.VERBOSE : LogLevel.OFF,
			config: shakaPluginConfig
		};
	}

}